// @ts-nocheck
import {
  Injectable, Inject, Output, EventEmitter,
} from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { P, UIKit } from '../ui-kit';
import { LoggerService } from './logger.service';

@Injectable({
  providedIn: 'root',
})
export class RestApiService {
  // tslint:disable-next-line: no-output-native
  @Output() error: EventEmitter<HttpErrorResponse> = new EventEmitter();

  @Output() identity: EventEmitter<P.AuthAccreditation> = new EventEmitter();

  /**
   * accreditation
   */
  get accreditation(): P.AuthAccreditation {
    return this.authAccreditation;
  }

  set accreditation(accreditation: P.AuthAccreditation) {
    this.authAccreditation = accreditation;
    this.identity.emit(accreditation);
  }

  /**
   * api url (read only)
   */
  get url(): string {
    if (this.config?.urls?.api) {
      return this.config.urls.api;
    }
    switch (this.config.environment) {
      case 'local':
        return 'http://api.proportunity.local';
      case 'staging':
        return 'https://my-api-staging.proportunity.co';
      case 'production':
      default:
        return 'https://my-api.proportunity.co';
    }
  }

  //
  protected authAccreditation: P.AuthAccreditation;

  constructor(
    @Inject('config') protected config: UIKit.UIKITConfig,
    protected http: HttpClient,
    protected logger: LoggerService,
  ) { }

  /**
   *
   */
  initialAccreditation(accreditation: P.AuthAccreditation) {
    this.authAccreditation = accreditation;
  }

  // ------------------------------------------------------------ HEALTH

  /**
   *
   */
  status(): Observable<P.HealthStatus> {
    return this.http
      .get(`${this.url}`, { headers: this.headers() })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ AUTH

  /**
   * accredit access token
   */
  public accredit(token: string): Observable<P.AuthAccreditation> {
    return this.http
      .post(`${this.url}/auth/accredit`, {}, { headers: this.headers(token) })
      .pipe(
        tap((accreditation) => this.accreditation = accreditation as P.AuthAccreditation),
        catchError(this.handleError.bind(this)),
      );
  }

  /**
   * sign in user
   */
  public signIn(credentials: P.AuthCredentials): Observable<P.AuthAccreditation> {
    return this.http
      .post(`${this.url}/auth/sign-in`, credentials, {
        headers: this.headers(),
      })
      .pipe(
        tap((accreditation) => this.accreditation = accreditation as P.AuthAccreditation),
        catchError(this.handleError.bind(this)),
      );
  }

  /**
   * sign out user
   */
  public signOut(token: string = this.accreditation?.token): Observable<P.OperationResponse> {
    return this.http
      .post(
        `${this.url}/auth/sign-out`,
        { token },
        { headers: this.headers(token) },
      )
      .pipe(
        tap(() => this.accreditation = void 0),
        catchError(this.handleError.bind(this)),
      );
  }

  /**
   * set user password | activate user
   */
  public setPassword(
    credentials: { password: string; password_confirmation: string },
    token: string = this.accreditation?.token,
  ): Observable<P.AuthAccreditation> {
    return this.http
      .post(`${this.url}/auth/set-password`, credentials, {
        headers: this.headers(token),
      })
      .pipe(
        tap((accreditation) => this.accreditation = accreditation as P.AuthAccreditation),
        catchError(this.handleError.bind(this)),
      );
  }

  /**
   * change user password
   */
  public changePassword(
    credentials: {
      password: string;
      new_password: string;
      new_password_confirmation: string;
    },
    token: string = this.accreditation?.token,
  ): Observable<P.User<P.Applicant | P.Advisor | P.Admin>> {
    return this.http
      .post(`${this.url}/auth/change-password`, credentials, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * request reset password email
   */
  public requestResetPasswordEmail(email: string): Observable<any> {
    return this.http
      .post(
        `${this.url}/auth/request-reset-password`,
        { email },
        { headers: this.headers() },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * reset user password
   */
  public resetPassword(
    credentials: { password: string; password_confirmation: string },
    token: string = this.accreditation?.token,
  ): Observable<P.User<P.Applicant | P.Advisor | P.Admin>> {
    // make request
    return this.http
      .post(`${this.url}/auth/reset-password`, credentials, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * resent activation email
   */
  public resendActivationEmail(
    email: string,
    token: string = this.accreditation?.token,
  ): Observable<P.OperationResponse> {
    // make request
    return this.http
      .post(
        `${this.url}/auth/request-activation-link`,
        { email },
        { headers: this.headers(token) },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * validate access token
   */
  public validateToken(token: string = this.accreditation?.token): Observable<{ valid: boolean }> {
    return this.http
      .post(
        `${this.url}/auth/validate-token`,
        { token },
        { headers: this.headers(token) },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * refresh access token
   */
  public refreshAccessToken(token: string = this.accreditation?.refreshToken): Observable<string> {
    return this.http
      .post(
        `${this.url}/auth/refresh-token`,
        { token },
        { headers: this.headers(token) },
      )
      .pipe(
        tap((accessToken: string) => this.accreditation = { ...this.accreditation, token: accessToken }),
        catchError(this.handleError.bind(this)),
      );
  }

  // ------------------------------------------------------------ USER

  /**
   * get users – list
   */
  public getUsers(
    query: { [query: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.User<P.Applicant | P.Advisor | P.Admin>>> {
    return this.http
      .get(`${this.url}/user`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get user
   */
  public getUser(
    id: string,
    token: string = this.accreditation?.token,
  ): Observable<P.User<P.Applicant | P.Advisor | P.Admin>> {
    return this.http
      .get(`${this.url}/user/${id}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * update user
   */
  public updateUser(
    id: string,
    update: Partial<P.User<P.Applicant | P.Advisor | P.Admin>>,
    token: string = this.accreditation?.token,
    query: { [key: string]: string | string[] } = {},
  ): Observable<P.User<P.Applicant | P.Advisor | P.Admin>> {
    return this.http
      .patch(`${this.url}/user/${id}`, update, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * create user
   */
  public createUser(user: {
    first_name: string;
    last_name: string;
    email: string;
    agree_tanc: boolean;
    income?: number;
    income_extra?: number;
    savings?: number;
    phone?: string;
    referrer?: string;
    autopilot_session_id?: string;
  }, token: string = this.accreditation?.token): Observable<P.User<P.Applicant | P.Advisor | P.Admin>> {
    return this.http
      .post(`${this.url}/auth/sign-up`, user, {
        headers: this.headers(token), // `${this.url}/user`
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * remove user
   */
  public removeUser(
    id: string,
    token: string = this.accreditation?.token,
  ): Observable<P.OperationResponse> {
    return this.http
      .delete(`${this.url}/user/${id}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ APPLICATION

  /**
   * update application
   */
  public updateApplication(
    id: string,
    update: Partial<P.Application>,
    token: string = this.accreditation?.token,
  ): Observable<P.Application> {
    return this.http
      .patch(`${this.url}/application/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * add joint applicant to application
   */
  public addJointApplicant(
    applicationId: string,
    email: string,
    token: string = this.accreditation?.token,
  ): Observable<P.Application> {
    return this.http
      .post(
        `${this.url}/application/${applicationId}/add_joint_applicant`,
        { email },
        { headers: this.headers(token) },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * remove joint applicant from application
   */
  public removeJointApplicant(
    applicationId: string,
    jointApplicantId: string,
    token: string = this.accreditation?.token,
  ): Observable<P.Application> {
    return this.http
      .post(
        `${this.url}/application/${applicationId}/remove_joint_applicant`,
        { id: jointApplicantId },
        { headers: this.headers(token) },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  public postBrokerAssessment(
    applicationId: string,
    body: P.BrokerAssessment,
    token: string = this.accreditation?.token,
  ): Observable<any> {
    return this.http
      .post(
        `${this.url}/application/${applicationId}/qualify-lead`,
        body,
        { headers: this.headers(token) },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getMip(
    applicationId: string,
    token: string = this.accreditation?.token,
  ): Observable<{ key: string }> {
    return this.http
      .get(`${this.url}/application/${applicationId}/mip`, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getSegment(
    applicationId: string,
    isInHighDemand: boolean = false,
    token: string = this.accreditation?.token,
  ): Observable<{ segment: P.Segment }> {
    return this.http
      .post(`${this.url}/application/${applicationId}/segment`, {}, {
        headers: this.headers(token),
        params: new HttpParams({
          fromObject: isInHighDemand ? { is_in_high_demand_outside_london: true } : {},
        }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ PEA

  /**
   *
   */
  public createPea(
    pea: Partial<P.Pea>, // TODO: update create pea fields
    token: string = this.accreditation?.token,
  ): Observable<P.Pea> {
    return this.http
      .post(`${this.url}/pea`, pea, {
        headers: this.headers(token),
        responseType: 'arraybuffer',
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ DIP

  /**
   * get dips - list
   */
  public getDips(
    query: { [query: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Dip>> {
    return this.http
      .get(`${this.url}/dip`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   *
   */
  public createDip(
    dip: {
      dip: {

      };
      probe_id: string;
      lead_id: string;
    }, // TODO: update create dip fields
    token: string = this.accreditation?.token,
  ): Observable<any> {
    return this.http
      .post(`${this.url}/dip`, dip, {
        headers: this.headers(token),
        responseType: 'arraybuffer',
      })
      .pipe(
        map((response) => {
          const file = new Blob([response as BlobPart], {
            type: 'application/pdf',
          });
          const fileURL = URL.createObjectURL(file);
          return fileURL;
        }),
        catchError(this.handleError.bind(this)),
      );
  }

  /**
   * get brokers - list
   */
  public getBrokers(
    query: { [query: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Broker>> {
    return this.http
      .get(`${this.url}/broker`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get broker
   */
  public getBroker(id: string, token: string = this.accreditation?.token): Observable<P.Broker> {
    return this.http
      .get(`${this.url}/broker/${id}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * update broker
   */
  public updateBroker(
    id: string,
    update: Partial<P.Broker>,
    token: string = this.accreditation?.token,
  ): Observable<P.Broker> {
    return this.http
      .patch(`${this.url}/broker/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * create broker
   */
  public createBroker(
    broker: Partial<P.Broker>, // TODO: update create broker fields
    token: string = this.accreditation?.token,
  ): Observable<P.Broker> {
    return this.http
      .post(`${this.url}/broker`, broker, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * remove broker
   */
  public removeBroker(
    id: string,
    token: string = this.accreditation?.token,
  ): Observable<P.OperationResponse> {
    return this.http
      .delete(`${this.url}/broker/${id}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get brokers - list
   */
  public getBrokerReferralCount(
    id: string,
    query: { [query: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<{
    total: number;
    byBroker: number;
    byProportunity: {
      total: number;
      byApplicant: number;
    }
  }> {
    return this.http
      .get(`${this.url}/broker/${id}/referral-count`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ ENQUIRY

  /**
   * get enquiries - list
   */
  public getEnquiries(
    query: { [query: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Enquiry>> {
    return this.http
      .get(`${this.url}/probe`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get enquiry
   */
  public getEnquiry(id: string, token: string = this.accreditation?.token): Observable<P.Enquiry> {
    return this.http
      .get(`${this.url}/probe/${id}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * create enquiry
   */
  public createEnquiry(
    applicationId: string,
    enquiry: Partial<P.Enquiry>, // TODO: update create enquiry fields
    token: string = this.accreditation?.token,
  ): Observable<P.Enquiry> {
    return this.http
      .post(`${this.url}/probe/${applicationId}`, enquiry, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * update enquiry
   */
  public updateEnquiry(
    id: string,
    update: Partial<P.Enquiry>,
    token: string = this.accreditation?.token,
  ): Observable<P.Enquiry> {
    return this.http
      .patch(`${this.url}/probe/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * create public enquiry
   */
  public createPublicEnquiry(publicEnquiry: {
    email: string;
    url: string;
  }): Observable<P.OperationResponse> {
    return this.http
      .post(`${this.url}/public/enquire`, publicEnquiry, {
        headers: this.headers(),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ FILES

  /**
   * upload file
   */
  public uploadFile(file: File, token: string = this.accreditation?.token): Observable<Blob> {
    return this.http
      .post(`${this.url}/file`, file, {
        headers: this.headers(token, { 'Content-Type': 'multipart/form-data' }),
        responseType: 'blob' as 'json',
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get file
   */
  public getFile(key: string, token: string = this.accreditation?.token): Observable<any> {
    return this.http
      .get(`${this.url}/file/${key}`, {
        headers: this.headers(token),
        responseType: 'blob' as 'json',
        // responseType: 'arraybuffer'
      })
      .pipe(
        // map(response => {
        //   const file = new Blob([response as BlobPart], {
        //     // type: 'application/dataurl'
        //   });
        //   const fileURL = URL.createObjectURL(file);
        //   return fileURL;
        // }),
        catchError(this.handleError.bind(this)),
      );
  }

  public patchFile_admin(
    id: string,
    update: Partial<P.ApplicationFile>,
    token: string = this.accreditation?.token,
  ): Observable<P.ApplicationFile> {
    return this.http
      .patch(`${this.url}/admin/file/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      );
  }

  /**
   * get file from url
   * token (optional)
   */
  public getFileFromUrl(url: string, token: string = this.accreditation?.token): Observable<any> {
    return this.http
      .get(url, {
        headers: this.headers(token),
        responseType: 'blob' as 'json',
        // responseType: 'arraybuffer'
      })
      .pipe(
        // map(response => {
        //   const file = new Blob([response as BlobPart], {
        //     // type: 'application/dataurl'
        //   });
        //   const fileURL = URL.createObjectURL(file);
        //   return fileURL;
        // }),
        catchError(this.handleError.bind(this)),
      );
  }

  // ------------------------------------------------------------ LOCATIONS

  /**
   * geocode locations
   */
  public geocodeLocation(query: {
    [query: string]: string | string[];
  }): Observable<{
    results: P.GeocodeResult[];
    status: string;
  }> {
    return this.http
      .get('https://maps.googleapis.com/maps/api/geocode/json', {
        params: new HttpParams({
          fromObject: {
            ...query,
            key: 'AIzaSyA-bGrIYIhRps7yxA5fEh05dmMkkh9NKwA', // TODO: change to env switch var
          },
        }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ POI

  /**
   * get points of interest
   * token (optional)
   */
  public getPOI(
    msoa: string,
    query: { [key: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<{ results: P.POIResult[]; msoa_code: string }> {
    return this.http
      .get(`${this.url}/poi/${msoa}`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ MSOA

  /**
   * get msoas
   */
  public getMsoas(
    query: { [key: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<any> {
    // TODO: type response
    return this.http
      .get(`${this.url}/msoa`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get msoa
   */
  public getMsoa(code: string, token: string = this.accreditation?.token): Observable<any> {
    // TODO: type response
    return this.http
      .get(`${this.url}/msoa/${code}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get london msoas
   * token (optional)
   */
  public getLondonMsoas(
    query: { [key: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<any> {
    // TODO: type response
    return this.http
      .post(`${this.url}/msoa/london`, query, {
        headers: this.headers(token),
        // params: new HttpParams({ fromObject: query })
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get london msoa
   * token (optional)
   */
  public getLondonMsoa(
    code: string,
    token: string = this.accreditation?.token,
  ): Observable<any> {
    // TODO: type response
    return this.http
      .get(`${this.url}/msoa/${code}/london`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get london msoa by postcode
   * token (optional)
   */
  public getLondonMsoaByPostcode(
    postcode: string,
    token: string = this.accreditation?.token,
  ): Observable<any> {
    // TODO: type response
    return this.http
      .get(`${this.url}/msoa/postcode/${postcode}`, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ RANK

  /**
   * get msoa ranks
   * token (optional)
   */
  public getRanks(
    query: { [key: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<any> {
    // TODO: type response
    return this.http
      .get(`${this.url}/msoa/rank`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get msoa rank
   * token (optional)
   */
  public getRank(
    msoa: string,
    token: string = this.accreditation?.token,
  ): Observable<any> {
    // TODO: type response
    return this.http
      .get(`${this.url}/msoa/rank/${msoa}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ LISTING

  /**
   * get listings - list
   * token (optional)
   */
  public getListings(
    query: { [key: string]: string | string[] | number | number[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Listing>> {
    return this.http
      .get(`${this.url}/listing`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get listings
   * token (optional)
   */
  public getListing(
    id: string,
    token: string = this.accreditation?.token,
  ): Observable<P.ListingExtended> {
    return this.http
      .get(`${this.url}/listing/${id}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * update listing
   */
  public updateListing(
    id: string,
    listing: Partial<P.Listing>,
    token: string = this.accreditation?.token,
  ): Observable<P.Listing> {
    return this.http
      .patch(`${this.url}/listing/${id}`, listing, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get available listings count
   * token (optional)
   */
  public getAvailableListingsCount(
    query: { [key: string]: string | string[] | number[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<{ [key: string]: number; }> {
    return this.http
      .get(`${this.url}/listing/homecount`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public addExternalListing_admin(
    body: { url: string },
    token: string = this.accreditation?.token,
  ): Observable<{ missingFields: boolean, listing: P.Listing }> {
    return this.http
      .post(`${this.url}/admin/listing/confirm-manual-eligibility`, body, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ SUGGESTIONS

  /**
   * get suggestions - list
   * token (optional)
   */
  public getSuggestions(
    query: { [key: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Listing>> {
    return this.http
      .get(`${this.url}/suggestion/`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get suggestions by listing – list
   * token (optional)
   */
  public getListingSuggestions(
    id: string,
    query: { [key: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Listing[]> {
    return this.http
      .get(`${this.url}/suggestion/listing/${id}`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get suggestions by user - list
   */
  public getUserSuggestions(
    id: string,
    query: { [key: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Listing>> {
    return this.http
      .get(`${this.url}/suggestion/user/${id}`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ ALERT

  /**
   * get alerts – list
   */
  public getAlerts(
    query: { [key: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Alert>> {
    return this.http
      .get(`${this.url}/alert/`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get alert
   */
  public getAlert(id: string, token: string = this.accreditation?.token): Observable<P.Alert> {
    return this.http
      .get(`${this.url}/alert/${id}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * update alert
   */
  public updateAlert(
    id: string,
    update: Partial<P.Alert>,
    token: string = this.accreditation?.token,
  ): Observable<P.Alert> {
    return this.http
      .patch(`${this.url}/alert/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * create alert
   */
  public createAlert(
    alert: Partial<P.Alert>, // TODO:
    token: string = this.accreditation?.token,
  ): Observable<P.Alert> {
    return this.http
      .post(`${this.url}/alert/`, alert, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * delete alert
   */
  public deleteAlert(
    id: string,
    token: string = this.accreditation?.token,
  ): Observable<P.OperationResponse> {
    return this.http
      .delete(`${this.url}/alert/${id}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ EVENTS

  /**
   * get event - list
   */
  public getEvents(token: string = this.accreditation?.token): Observable<P.Page<P.Event>> {
    return this.http
      .get(`${this.url}/event/`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * create event
   */
  public createEvent(
    event: Partial<P.Event>, // TODO:
    token: string = this.accreditation?.token,
  ): Observable<P.Event> {
    return this.http
      .post(`${this.url}/event`, event, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ TAGS

  /**
   * get tags - list
   */
  public getTags_admin(
    query: { [query: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Tag>> {
    return this.http
      .get(`${this.url}/admin/tags`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get tag
   */
  public getTag_admin(id: string, token: string = this.accreditation?.token): Observable<P.Tag> {
    return this.http
      .get(`${this.url}/admin/tags/${id}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * create tag
   */
  public createTag_admin(
    tag: Partial<P.Tag>,
    token: string = this.accreditation?.token,
  ): Observable<P.Tag> {
    return this.http
      .post(`${this.url}/admin/tags`, tag, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * update enquiry
   */
  public updateTag_admin(
    id: string,
    update: Partial<P.Tag>,
    token: string = this.accreditation?.token,
  ): Observable<P.Tag> {
    return this.http
      .patch(`${this.url}/admin/tags/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ CALLS

  /**
   * get calls – list
   */
  public getCalls(token: string = this.accreditation?.token): Observable<P.Page<P.Call>> {
    return this.http
      .get(`${this.url}/call/`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get call
   */
  public getCall(id: string, token: string = this.accreditation?.token): Observable<P.Call> {
    return this.http
      .get(`${this.url}/call/${id}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * create an outbound call
   */
  public createOutboundCall_admin(
    body: { user: string },
    token: string = this.accreditation?.token,
  ): Observable<P.Call> {
    return this.http
      .post(`${this.url}/admin/call/create-outbound-call`, body, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ SURVEY

  /**
   * get survey – list
   */
  public getSurveys(token: string = this.accreditation?.token): Observable<P.Page<P.Survey>> {
    return this.http
      .get(`${this.url}/survey/`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get survey
   */
  public getSurvey(id: string, token: string = this.accreditation?.token): Observable<P.Survey> {
    return this.http
      .get(`${this.url}/survey/${id}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * create survey
   */
  public createSurvey(
    survey: Partial<P.Survey>, // TODO:
    token: string = this.accreditation?.token,
  ): Observable<P.Survey> {
    return this.http
      .post(`${this.url}/survey`, survey, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * update survey
   */
  public updateSurvey(
    id: string,
    update: Partial<P.Survey>,
    token: string = this.accreditation?.token,
  ): Observable<P.Survey> {
    return this.http
      .patch(`${this.url}/survey/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ SURVEY ANSWER

  /**
   * get survey answer
   */
  public getSurveyAnswer(
    surveyId: string,
    id: string,
    token: string = this.accreditation?.token,
  ): Observable<P.SurveyAnswer> {
    return this.http
      .get(`${this.url}/survey/${surveyId}/${id}`, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * create survey answer
   */
  public createSurveyAnswer(
    id: string,
    answer: Partial<P.SurveyAnswer>, // TODO:
    token: string = this.accreditation?.token,
  ): Observable<P.SurveyAnswer> {
    return this.http
      .post(`${this.url}/survey/${id}`, answer, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * update survey answer
   */
  public updateSurveyAnswer(
    surveyId: string,
    id: string,
    update: Partial<P.SurveyAnswer>,
    token: string = this.accreditation?.token,
  ): Observable<P.SurveyAnswer> {
    return this.http
      .patch(`${this.url}/survey/${surveyId}/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * create survey response
   */
  public createSurveyResponse(
    surveyId: string,
    answerId: any,
    surveyToken: string,
    body: any = {},
  ): Observable<P.SurveyResponse> {
    return this.http
      .post(
        `${this.url}/survey/${surveyId}/${answerId}?t=${surveyToken}`,
        body,
        { headers: this.headers(surveyToken) },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ LOAN QUEUE

  /**
   * get upcoming loan queue slots for amount
   */
  public getLoanQueueSlots(
    query: {
      amount?: number | string,
      available?: boolean | string
    },
    token: string = this.accreditation?.token,
  ): Observable<P.LoanQueueSlot[]> {
    return this.http
      .get(`${this.url}/loan-queue/`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query as { [key: string]: string | string[] } }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * request loan queue allocation
   */
  public requestLoanQueueSlotAllocation(
    loanQueueRequest: P.LoanQueueRequest,
    token: string = this.accreditation?.token,
  ): Observable<P.LoanQueueResponse> {
    return this.http
      .post(`${this.url}/loan-queue-allocation/`, loanQueueRequest, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ NOTES

  /**
   *
   */
  public createNote(
    note: {
      note: string,
      author: string,
      author_type: string,
      application: string
    },
    token: string = this.accreditation?.token,
  ): Observable<P.Note> {
    return this.http
      .post(`${this.url}/note`, note, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ ELIGIBILITY (PROPERTY CHECKER)

  /**
   *
   */
  public getEligibility(
    data: {
      price: number,
      postcode: string,
      surface_area: number,
      property_type: string
    },
    token: string = this.accreditation?.token,
  ): Observable<any> {
    return this.http
      .post(`${this.url}/listing/eligible`, data, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ PRODUCTS

  /**
   *
   */
  public getProducts(
    query: P.Dictionary<string | number>,
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.LoanProduct>> {
    return this.http
      .get(`${this.url}/product`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query as { [key: string]: string | string[] } }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   *
   */
  public getProduct(
    id: string,
    token: string = this.accreditation?.token,
  ): Observable<P.LoanProduct> {
    return this.http
      .get(`${this.url}/product/${id}`, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ INTERMIDIARIES

  /**
   *
   */
  public inviteLeadToApplicant(
    id: string,
    data: { email: string },
    token: string = this.accreditation?.token,
  ): Observable<P.Alert[]> {
    return this.http
      .post(`${this.url}/leads/${id}/invite`, data, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * get alerts – list
   */
  public getAIntermediaryAlerts(
    query: { [key: string]: string | string[] } = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Alert[]> {
    return this.http
      .get(`${this.url}/alert/intermediaries`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   *
   */
  public createIntermediaryLead(
    lead: {
      agent_referral?: string;
      agent_text?: string;
      email: string;
      first_name: string;
      income: number;
      income_extra?: number;
      last_name: string;
      phone?: string;
      savings: number;
    },
    token: string = this.accreditation?.token,
  ): Observable<P.User<P.LeadUser>> {
    return this.http
      .post(`${this.url}/leads/`, lead, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   *
   */
  public getIntermediaryLeads(
    query: P.Dictionary<string | number>,
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Application>> {
    return this.http
      .get(`${this.url}/leads/`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query as { [key: string]: string | string[] } }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   *
   */
  public getIntermediaryLead(
    id: string,
    params: P.Dictionary<string | number> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Application> {
    return this.http
      .get(`${this.url}/leads/${id}`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: params as { [key: string]: string | string[] } }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getLeadCalls(
    id: string,
    params: P.Dictionary<string | number> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Call[]> {
    return this.http
      .get(`${this.url}/leads/${id}/calls`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: params as { [key: string]: string | string[] } }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public updateLeadCall(
    id: string,
    callId: string,
    update: Partial<P.Call>,
    token: string = this.accreditation?.token,
  ): Observable<P.Call> {
    return this.http.patch(`${this.url}/leads/${id}/calls/${callId}`, update, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public createOutboundLeadCall(
    id: string,
    token: string = this.accreditation?.token,
  ): Observable<P.Call> {
    return this.http.post(`${this.url}/leads/${id}/outbound-call`, {}, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   *
   */
  public patchIntermediaryLead(
    id: string,
    update: Partial<P.User<P.Applicant>>,
    token: string = this.accreditation?.token,
  ): Observable<P.User<P.Applicant>> {
    return this.http
      .patch(`${this.url}/leads/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   *
   */
  public referToSecondndCharge(
    id: string,
    data: Partial<P.LoanApplication> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.LoanApplication> {
    return this.http
      .post(`${this.url}/loan-application/${id}/refer-to-second-change`, data, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   *
   */
  public requestEsis(
    id: string,
    data: Partial<P.LoanApplication> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.LoanApplication> {
    return this.http
      .post(`${this.url}/loan-application/${id}/request-esis`, data, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public generateEsis(
    id: string,
    data: Partial<P.LoanApplication> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.LoanApplication> {
    return this.http
      .post(`${this.url}/loan-application/${id}/generate-esis`, data, {
        headers: this.headers(token),
        responseType: 'arraybuffer',
      })
      .pipe(
        map((response) => {
          const file = new Blob([response as BlobPart], {
            type: 'text/csv',
          });
          return URL.createObjectURL(file);
        }),
        catchError(this.handleError.bind(this)),
      );
  }

  public generateWelcomeLetter(id: string, token: string = this.accreditation?.token): Observable<P.LoanApplication> {
    return this.http.post(`${this.url}/loan-application/${id}/generate-welcome-letter`, {}, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getLatestValuation(id: string, token: string = this.accreditation?.token): Observable<P.PortfolioRow> {
    return this.http
      .get(`${this.url}/loan-application/${id}/latest-valuation`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public updateLatestValuation(id: string, data: Partial<PortfolioRow>, token: string = this.accreditation?.token): Observable<P.PortfolioRow> {
    return this.http
      .post(`${this.url}/loan-application/${id}/latest-valuation`,
        data,
        { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public generateAnnualStatment(loanApplicationId: string, token: string = this.accreditation?.token): Observable<P.LoanApplication> {
    return this.http.post(`${this.url}/loan-application/generate-annual-statement`, {
      loanApplicationId
    }, {
      headers: this.headers(token)
    })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public downloadActiveLoanData(funder: string, token: string = this.accreditation?.token): Observable<string> {
    return this.http
      .get(`${this.url}/loan-application/export-active-loans`, {
        headers: this.headers(token),
        responseType: 'arraybuffer',
        params: new HttpParams({ fromObject: { funder } }),
      })
      .pipe(
        map((response) => {
          const file = new Blob([response as BlobPart], {
            type: 'text/csv',
          });
          return URL.createObjectURL(file);
        }),
        catchError(this.handleError.bind(this)),
      );
  }

  public downloadWaterfallActiveLoanData(token: string = this.accreditation?.token): Observable<string> {
    return this.http
      .get(`${this.url}/loan-application/export-waterfall-active-loans`, {
        headers: this.headers(token),
        responseType: 'arraybuffer',
      })
      .pipe(
        map((response) => {
          const file = new Blob([response as BlobPart], {
            type: 'text/csv',
          });
          return URL.createObjectURL(file);
        }),
        catchError(this.handleError.bind(this)),
      );
  }

  /**
   *
   */
  public getLendingSettings_admin(
    query: P.Dictionary<any>,
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.LendingSettings>> {
    return this.http
      .get(`${this.url}/admin/lending-settings/`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query as { [key: string]: string | string[] } }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
  *
  */
  public getLendingFunders_admin(
    query: P.Dictionary<any>,
    token: string = this.accreditation?.token,
  ): Observable<string[]> {
    return this.http
      .get(`${this.url}/admin/lending-funders/`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query as { [key: string]: string | string[] } }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   *
   */
  public createLendingSettings_admin(
    data: Partial<P.LendingSettings>,
    token: string = this.accreditation?.token,
  ): Observable<P.LendingSettings> {
    return this.http
      .post(`${this.url}/admin/lending-settings/`, data, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getToolsSettings_admin(
    token: string = this.accreditation?.token,
  ): Observable<P.ToolsSettings> {
    return this.http
      .get(`${this.url}/admin/tools-settings/`, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public postToolsSettings_admin(
    data: Partial<P.ToolsSettings>,
    token: string = this.accreditation?.token,
  ): Observable<P.ToolsSettings> {
    return this.http
      .post(`${this.url}/admin/tools-settings/`, data, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   *
   */
  public downloadApplicationForm(
    id: string,
    data: { [key: string]: any } = {},
    token: string = this.accreditation?.token,
  ): Observable<Blob> {
    return this.http
      .post(`${this.url}/loan-application/${id}/application-form`, data, {
        headers: this.headers(token),
        responseType: 'arraybuffer',
      })
      .pipe(
        map((response) => {
          const file = new Blob([response as BlobPart], {
            type: 'application/pdf',
          });
          const fileURL = URL.createObjectURL(file);
          return fileURL;
        }),
        catchError(this.handleError.bind(this)),
      );
  }

  /**
   *
   */
  public downloadIODForm(
    id: string,
    data: { [key: string]: any } = {},
    token: string = this.accreditation?.token,
  ): Observable<Blob> {
    return this.http
      .post(`${this.url}/loan-application/${id}/interestdec-form`, data, {
        headers: this.headers(token),
        responseType: 'arraybuffer',
      })
      .pipe(
        map((response) => {
          const file = new Blob([response as BlobPart], {
            type: 'application/pdf',
          });
          const fileURL = URL.createObjectURL(file);
          return fileURL;
        }),
        catchError(this.handleError.bind(this)),
      );
  }

  /**
   *
   */
  public createIntermediaryApplication(
    data: Partial<P.LoanApplication>,
    token: string = this.accreditation?.token,
  ): Observable<P.LoanApplication> {
    return this.http
      .post(`${this.url}/loan-application/`, data, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   *
   */
  public getIntermediaryApplications(
    query: P.Dictionary<any>,
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.LoanApplication>> {
    return this.http
      .get(`${this.url}/loan-application/`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query as { [key: string]: string | string[] } }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   *
   */
  public getIntermediaryApplication(
    id: string,
    params: P.Dictionary<any> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.LoanApplication> {
    return this.http
      .get(`${this.url}/loan-application/${id}`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: params as { [key: string]: string | string[] } }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   *
   */
  public patchIntermediaryApplication(
    id: string,
    update: Partial<P.LoanApplication>,
    token: string = this.accreditation?.token,
  ): Observable<P.LoanApplication> {
    return this.http
      .patch(`${this.url}/loan-application/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
  *
  */
  public cancelIntermediaryApplication(
    id: string,
    token: string = this.accreditation?.token,
  ): Observable<P.LoanApplication> {
    return this.http
      .delete(`${this.url}/loan-application/${id}`, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * INTEGRATIONS - MBT
   */

  public getOrCreateBroker_mbt(
    caseId: string,
    brokerId: string,
    params: P.Dictionary<any>,
    token: string = this.accreditation?.token,
  ): Observable<P.Broker> {
    return this.http
      .get(`${this.url}/integrations/v1/mbt/broker/${caseId}/${brokerId}`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: params as { [key: string]: string | string[] } }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getOrCreateAdvisor_mbt(
    caseId: string,
    params: P.Dictionary<any>,
    token: string = this.accreditation?.token,
  ): Observable<{
    activationToken?: string;
    user: P.User<P.Advisor>;
  }> {
    return this.http
      .get(`${this.url}/integrations/v1/mbt/advisor/${caseId}`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: params as { [key: string]: string | string[] } }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getOrCreateLead_mbt(
    caseId: string,
    params: P.Dictionary<any>,
    token: string = this.accreditation?.token,
  ): Observable<any> {
    return this.http
      .get(`${this.url}/integrations/v1/mbt/lead/${caseId}`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: params as { [key: string]: string | string[] } }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ AGENT REFERRAL

  /**
   *
   */
  public agentReferral(data: any, token: string = this.accreditation?.token): Observable<any> {
    return this.http
      .post(`${this.url}/agent_referral`, data, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // ------------------------------------------------------------ END

  /**
   * generate https headers
   */
  protected headers(
    token: string = null,
    extras: HttpHeaders | { [header: string]: string | string[] } = {},
  ): HttpHeaders | { [header: string]: string | string[] } {
    // collection of headers
    let headers: HttpHeaders | { [header: string]: string | string[] } = {
    };
    // if token, add authorization token bearer
    if (token) {
      headers = {
        ...headers,
        Authorization: `Bearer ${token}`,
      };
    }
    // return headers
    return { ...headers, ...extras };
  }

  /**
   *
   */
  protected options(): {
    headers?:
    | HttpHeaders
    | {
      [header: string]: string | string[];
    };
    observe?: 'body';
    params?:
    | HttpParams
    | {
      [param: string]: string | string[];
    };
    reportProgress?: boolean;
    responseType?: 'arraybuffer';
    withCredentials?: boolean;
  } {
    return {};
  }

  /**
   * ADMIN ONLY
   */

  public createUser_admin(
    data: Partial<P.User<P.Applicant | P.Advisor | P.Admin>>,
    token: string = this.accreditation?.token,
  ): Observable<P.User<P.Applicant | P.Advisor | P.Admin>> {
    return this.http
      .post(`${this.url}/admin/user`, data, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.User<P.Applicant | P.Advisor | P.Admin>>;
  }

  public listFavourites_admin(
    query: UIKit.Dictionary<string> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Enquiry>> {
    return this.http
      .get(`${this.url}/admin/probe`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Page<P.Enquiry>>;
  }

  public getFavourite_admin(id: string, token: string = this.accreditation?.token): Observable<P.Enquiry> {
    return this.http
      .get(`${this.url}/admin/probe/${id}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public updateFavourite_admin(
    id: string,
    update: Partial<P.Enquiry>,
    token: string = this.accreditation?.token,
  ): Observable<P.Enquiry> {
    return this.http
      .patch(`${this.url}/admin/probe/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public createFavourite_admin(
    applicationId: string,
    enquiry: Partial<P.Enquiry>, // TODO: update create enquiry fields
    token: string = this.accreditation?.token,
  ): Observable<P.Enquiry> {
    return this.http
      .post(`${this.url}/admin/probe/${applicationId}`, enquiry, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public listRegulatoryReport_admin(
    token: string = this.accreditation?.token,
  ): Observable<P.Report[]> {
    return this.http
      .get(`${this.url}/admin/regulatory-reports`, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Report[]>;
  }

  public generateEquifaxReport_admin(
    query: UIKit.Dictionary<string> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Report[]> {
    return this.http
      .get(`${this.url}/admin/regulatory-reports/generate/equifax`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Report[]>;
  }

  public generateRegulatoryReport_admin(
    data: { type: 'quarter' | 'annual', quarter?: string },
    token: string = this.accreditation?.token,
  ): Observable<P.Report> {
    return this.http
      .post(`${this.url}/admin/regulatory-reports/generate`, data, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Report>;
  }

  public getRegulatoryReport_admin(
    key: string,
    token: string = this.accreditation?.token,
  ): Observable<any> {
    return this.http
      .get(`${this.url}/admin/file/reports/${key}`, {
        headers: this.headers(token),
        responseType: 'blob' as 'json',
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getAccountsInArrears_admin(token: string = this.accreditation?.token): Observable<any> {
    return this.http
      .get(`${this.url}/admin/loan-application/arrears`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public ignorePaymentEvent(id: string, token: string = this.accreditation?.token): Observable<any> {
    return this.http
      .post(`${this.url}/admin/payment-events/${id}/ignore`, null, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getAdvisors_admin(
    query: UIKit.Dictionary<string> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.User<P.Advisor>[]> {
    return this.http
      .get(`${this.url}/admin/user/advisors`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.User<P.Advisor>[]>;
  }

  public listUsers_admin(
    query: UIKit.Dictionary<string> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.User<P.Applicant | P.Advisor | P.Admin>>> {
    return this.http
      .get(`${this.url}/admin/user`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Page<P.User<P.Applicant | P.Advisor | P.Admin>>>;
  }

  public getUser_admin(
    id: string,
    query: UIKit.Dictionary<string> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.User<P.Applicant | P.Advisor | P.Admin>> {
    return this.http
      .get(`${this.url}/admin/user/${id}`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.User<P.Applicant | P.Advisor | P.Admin>>;
  }

  public updateUser_admin(
    id: string,
    update: UIKit.Dictionary<any>,
    token: string = this.accreditation?.token,
  ): Observable<P.User<P.Applicant | P.Advisor | P.Admin>> {
    return this.http
      .patch(`${this.url}/admin/user/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.User<P.Applicant | P.Advisor | P.Admin>>;
  }

  public getBrokerStats_admin(
    query: UIKit.Dictionary<string> = {
      // populateApplication: boolean
    },
    token: string = this.accreditation?.token,
  ): Observable<P.BrokerStats[]> {
    return this.http
      .get(`${this.url}/admin/broker/stats`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.BrokerStats[]>;
  }

  public getBrokerIntroStats_admin(
    query: UIKit.Dictionary<string> = {
      // populateApplication: boolean
    },
    token: string = this.accreditation?.token,
  ): Observable<P.BrokerIntroStats[]> {
    return this.http
      .get(`${this.url}/admin/broker/intro-stats`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.BrokerIntroStats[]>;
  }

  public generateLink_admin(
    data: {
      expiresIn: number;
      linkType: 'activation' | 'reset_password' | 'survey' | 'broker_select';
      platform: 'client' | 'intermediaries' | 'admin';
      user: string;
    },
    token: string = this.accreditation?.token,
  ): Observable<{ link: string; }> {
    return this.http
      .post(`${this.url}/admin/user/generate-system-link`, data, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<{ link: string; }>;
  }

  public generateToken_admin(
    data: {
      expiresIn: number;
      linkType: 'activation' | 'reset_password' | 'survey' | 'broker_select';
      platform: 'client' | 'intermediaries' | 'admin';
      user: string;
    },
    token: string = this.accreditation?.token,
  ): Observable<{ token: string; }> {
    return this.http
      .post(`${this.url}/admin/user/generate-system-token`, data, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<{ token: string; }>;
  }

  public removeUserTokens_admin(
    data: {
      user: string
    },
    token: string = this.accreditation?.token,
  ): Observable<{ status: string; }> {
    return this.http
      .post(`${this.url}/admin/user/remove-user-tokens`, data, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<{ status: string; }>;
  }

  public referToBroker(
    data: { application: string; broker: string, bcc?: string, sendEmail?: boolean; },
    token: string = this.accreditation?.token,
  ): Observable<any> {
    return this.http
      .post(`${this.url}/application/refer-to-broker`, data, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this))) as Observable<any>;
  }

  public referToBroker_admin(
    data: { application: string; broker: string, bcc?: string, sendEmail?: boolean; },
    token: string = this.accreditation?.token,
  ): Observable<any> {
    return this.http
      .post(`${this.url}/admin/util/refer-to-broker`, data, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this))) as Observable<any>;
  }

  public unReferFromBroker_admin(
    application: string,
    data: { status: string; 'un-refer': boolean; } = {
      status: 'Open',
      'un-refer': true,
    },
    token: string = this.accreditation?.token,
  ): Observable<any> {
    return this.http
      .patch(`${this.url}/admin/application/${application}/status`, data, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this))) as Observable<any>;
  }

  public sendBrokerChoiceEmail_admin(
    data: { application: string; brokerChoice: string[], content: string, bcc?: string },
    token: string = this.accreditation?.token,
  ): Observable<any> {
    return this.http
      .post(`${this.url}/admin/util/broker-choice-email`, data, {
        headers: this.headers(token),
      })
      .pipe(catchError(this.handleError.bind(this))) as Observable<any>;
  }

  public getUnassignedApplications_admin(
    query: UIKit.Dictionary<string> = {},
    token: string = this.accreditation.token,
  ): Observable<P.Page<P.Application>> {
    return this.http
      .get(`${this.url}/admin/application/unassigned`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Page<P.Application>>;
  }

  public getEmailMacros_admin(
    query: UIKit.Dictionary<string> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.EmailMacro>> {
    return this.http
      .get(`${this.url}/admin/email-macro`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Page<P.EmailMacro>>;
  }

  public createEmailMacro_admin(
    data: P.EmailMacro,
    token: string = this.accreditation?.token,
  ): Observable<P.EmailMacro> {
    return this.http
      .post(`${this.url}/admin/email-macro`, data, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.EmailMacro>;
  }

  public updateEmailMacro_admin(
    id: string,
    update: Partial<P.EmailMacro>,
    token: string = this.accreditation?.token,
  ): Observable<P.EmailMacro> {
    return this.http
      .patch(`${this.url}/admin/email-macro/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.EmailMacro>;
  }

  public calculatePPSQM_admin(
    data: {
      postcode: string;
      price: number;
      property_type: string;
      surface_area: number;
      msoa_selected?: string;
    },
    token: string = this.accreditation?.token,
  ): Observable<P.Forecast> {
    return this.http
      .post(`${this.url}/admin/listing/forecast`, data, { headers: this.headers(token) })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Forecast>;
  }

  public createNote_admin(
    note: {
      note: string;
      application: string;
      noteType: P.NoteType;
    },
    token: string = this.accreditation?.token,
  ): Observable<P.Note> {
    return this.http
      .post(`${this.url}/admin/note`, note, { headers: this.headers(token) })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Note>;
  }

  public getAllCalls_admin(
    query: UIKit.Dictionary<string> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Call[]> {
    return this.http
      .get(`${this.url}/admin/call`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Call[]>;
  }

  public getAllCallsById_admin(
    ids: string[],
    token: string = this.accreditation?.token,
  ): Observable<P.Call[]> {
    return this.http
      .get(`${this.url}/admin/call`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: { user: ids.join(',') } }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Call[]>;
  }

  public updateCall_admin(
    id: string,
    update: Partial<P.Call>,
    token: string = this.accreditation?.token,
  ): Observable<P.Call> {
    return this.http
      .patch(`${this.url}/admin/call/${id}`, update, {
        headers: this.headers(token),
        // params: new HttpParams({ fromObject: { id } })
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Call>;
  }

  public assignCall_admin(
    id: string,
    token: string = this.accreditation?.token,
  ): Observable<P.Call[]> {
    return this.http
      .post(`${this.url}/admin/call/assign`, {}, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: { id } }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Call[]>;
  }

  public getPendingEnquiries_admin(
    query: UIKit.Dictionary<string> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.PendingEnquiry[]> {
    return this.http
      .get(`${this.url}/admin/util/pending-enquiries`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.PendingEnquiry[]>;
  }

  public listBrokers_admin(
    query: UIKit.Dictionary<string>,
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Broker>> {
    return this.http
      .get(`${this.url}/admin/broker/`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Page<P.Broker>>;
  }

  public getBroker_admin(
    id: string,
    query: UIKit.Dictionary<string> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Broker> {
    return this.http
      .get(`${this.url}/admin/broker/${id}`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Broker>;
  }

  public getLoanApplicationsPortfolio_admin(token: string = this.accreditation?.token): Observable<P.Broker> {
    return this.http.get(`${this.url}/admin/loan-application/portfolio`, {
      headers: this.headers(token),
      responseType: 'arraybuffer',
    })
      .pipe(
        map((response) => {
          const file = new Blob([response as BlobPart], {
            type: 'application/pdf',
          });
          return URL.createObjectURL(file);
        }),
        catchError(this.handleError.bind(this)),
      );
  }

  public createPortfolio_admin(uploadedFile: any, token: string = this.accreditation?.token): Observable<Blob> {
    // uploadedFile is respose from /file upload
    return this.http.post(`${this.url}/admin/portfolio`, uploadedFile, {
      headers: this.headers(token),
    })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getPortfolioPeriods_admin(token: string = this.accreditation?.token): Observable<string[]> {
    return this.http.get(`${this.url}/admin/portfolio/periods`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getPortfolio_admin(period: string, token: string = this.accreditation?.token): Observable<P.PortfolioRow[]> {
    return this.http.get(`${this.url}/admin/portfolio/${period}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * create broker
   */
  public createBroker_admin(
    broker: Partial<P.Broker>, // TODO: update create broker fields
    token: string = this.accreditation?.token,
  ): Observable<P.Broker> {
    return this.http
      .post(`${this.url}/admin/broker`, broker, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public updateBroker_admin(
    id: string,
    update: UIKit.Dictionary<any>,
    token: string = this.accreditation?.token,
  ): Observable<P.Broker> {
    return this.http
      .patch(`${this.url}/admin/broker/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Broker>;
  }

  public listCustomerProfiles_admin(
    query: UIKit.Dictionary<string>,
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Application>> {
    return this.http
      .get(`${this.url}/admin/application/`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Page<P.Application>>;
  }

  public joinApplications_admin(
    body: { mainApplicationId: string, secondApplicationId: string },
    token: string = this.accreditation?.token,
  ): Observable<P.Application> {
    return this.http
      .post(`${this.url}/admin/application/join`, body, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Application>;
  }

  public searchCustomerProfiles_admin(
    query: UIKit.Dictionary<string>,
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Application>> {
    return this.http
      .get(`${this.url}/admin/user/search`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Page<P.Application>>;
  }

  public getCustomerProfile_admin(
    id: string,
    query: UIKit.Dictionary<string> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Application> {
    return this.http
      .get(`${this.url}/admin/application/${id}`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Application>;
  }

  public updateCustomerProfile_admin(
    id: string,
    update: UIKit.Dictionary<any>,
    token: string = this.accreditation?.token,
  ): Observable<P.Application> {
    return this.http
      .patch(`${this.url}/admin/application/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Application>;
  }

  public updateCustomerEnquiry_admin(
    id: string,
    update: UIKit.Dictionary<any>,
    token: string = this.accreditation?.token,
  ): Observable<P.Enquiry> {
    return this.http
      .patch(`${this.url}/admin/probe/${id}`, update, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.Enquiry>;
  }

  public resolvePendingEnquiries_admin(
    resolve: {
      to: string;
      cc?: string;
      bcc?: string;
      html: string;
      approved: {
        id: string;
        product_group?: 'low_potential' | 'average_potential' | 'high_potential'
      }[];
      needinfo: { id: string; }[];
      rejected: {
        id: string;
        reasons?: {}
      }[];
    },
    token: string = this.accreditation?.token,
  ): Observable<{ success: boolean; }> {
    return this.http
      .post(`${this.url}/admin/util/resolve-enquiries`, resolve, {
        headers: this.headers(token),
      })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<{ success: boolean; }>;
  }

  /*
   * send text (sms)
   */
  public sendTextSms(sms: P.TwilioSms, token: string = this.accreditation?.token): Observable<P.TwilioResponse> {
    return this.http
      .post(`${this.url}/sms/send`, sms, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getPropertyRecommendations_admin(
    applicationId: string,
    token: string = this.accreditation?.token,
  ): Observable<P.PropertyRecommendation[]> {
    return this.http
      .get(`${this.url}/admin/property-recommendations?applicationId=${applicationId}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getRecommendedListings_admin(
    applicationId: string,
    query: UIKit.Dictionary<string> = {},
    token: string = this.accreditation?.token,
  ): Observable<P.Page<P.Listing>> {
    return this.http
      .get(`${this.url}/admin/listing/recommendations/${applicationId}`, {
        headers: this.headers(token),
        params: new HttpParams({ fromObject: query }),
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public addPropertyRecommendations_admin(
    body: { application: string, listings: string[] },
    token: string = this.accreditation?.token,
  ): Observable<P.PropertyRecommendation[]> {
    return this.http
      .post(`${this.url}/admin/property-recommendations`, body, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getPropertyRecommendations(
    token: string = this.accreditation?.token,
  ): Observable<P.PropertyRecommendation[]> {
    return this.http
      .get(`${this.url}/property-recommendations`, { headers: this.headers(token) })
      .pipe(
        catchError(this.handleError.bind(this)),
      ) as Observable<P.PropertyRecommendation[]>;
  }

  public setPropertyDislike(
    id: string,
    token: string = this.accreditation?.token,
  ): Observable<P.PropertyDislike> {
    return this.http
      .post(`${this.url}/property-dislike`, { listing: id }, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getPropertyDislikes(
    token: string = this.accreditation?.token,
  ): Observable<P.PropertyDislike[]> {
    return this.http
      .get(`${this.url}/property-dislike`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getPropertyDislikes_admin(
    applicationId: string,
    token: string = this.accreditation?.token,
  ): Observable<P.PropertyDislike[]> {
    return this.http
      .get(`${this.url}/admin/property-dislikes?applicationId=${applicationId}`, { headers: this.headers(token) })
      .pipe(catchError(this.handleError.bind(this)));
  }

  public qualifyLead_admin(
    applicationId: string,
    body: P.QualifiedByCS,
    token: string = this.accreditation?.token,
  ): Observable<any> {
    return this.http
      .post(
        `${this.url}/admin/application/${applicationId}/qualify-lead`,
        body,
        { headers: this.headers(token) },
      )
      .pipe(catchError(this.handleError.bind(this)));
  }

  public createLoanRecommendation(
    data: P.CreateLoanRecommendationBody,
    token: string = this.accreditation?.token,
  ): Observable<P.LoanRecommendation> {
    return this.http
      .post(`${this.url}/loan-recommendation`, data, {
        headers: this.headers(token),
      })
      .pipe(map((result) => result.recommendation))
      .pipe(catchError(this.handleError.bind(this)));
  }

  public getLoanRecommendation(
    id: string,
    token: string = this.accreditation?.token,
  ): Observable<P.LoanRecommendation> {
    return this.http
      .get(`${this.url}/loan-recommendation/${id}`, {
        headers: this.headers(token),
      })
      .pipe(map((result) => result.recommendation))
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * handle error
   */
  protected handleError(error: HttpErrorResponse): Observable<never> {
    // parse error message
    let message: string;
    if (error?.error?.byteLength !== undefined) {
      // parse error message when generate-ersi returns arraybuffer error
      const errorObjFromArrayBuffer = JSON.parse(String.fromCharCode.apply(null, new Uint8Array(error?.error)));
      message = errorObjFromArrayBuffer?.message;
    } else {
      message = error?.error?.message || 'An unexpected error has occurred; please try again later.';
    }
    const sanitizedError = { ...error, message };
    // propagate error
    this.error.emit(sanitizedError);

    // return error
    return throwError(sanitizedError);
  }
}
